import DonutPlot from "@/components/CategoricalPlots/barplot";
import type { SiteInfo } from "@/components/UI/SitesMap/types";
import {
  CardContainer,
  CardLayout,
} from "@/views/TrialHomeScreen/components/CardLayout";
import {
  CRFCard,
  MVRActionItemsCard,
  SiteSDVCard,
} from "@/views/TrialHomeScreen/sections/DataSection";
import { SitesOverviewCard } from "@/views/TrialHomeScreen/sections/EnrollmentSection";

type MetricsTabProps = {
  data?: SiteInfo[];
};
const numberOrZero = (value: unknown) => {
  return value != null && typeof value === "number" && Number.isFinite(value)
    ? value
    : 0;
};

const round = (value: number, decimals = 1) => {
  return Number(value.toFixed(decimals));
};

const sumBy = (sites: SiteInfo[], field: keyof SiteInfo) => {
  return sites.reduce((acc, site) => acc + numberOrZero(site[field]), 0);
};

const getNumericSeries = (sites: SiteInfo[], field: keyof SiteInfo) => {
  return sites
    .map((site) => numberOrZero(site[field]))
    .filter((value) => Number.isFinite(value));
};

const getStats = (series: number[]) => {
  if (!series.length) {
    return { average: 0, std: 0, min: 0, max: 0 };
  }
  const average = series.reduce((acc, value) => acc + value, 0) / series.length;
  const variance =
    series.reduce((acc, value) => acc + Math.pow(value - average, 2), 0) /
    series.length;
  return {
    average,
    std: Math.sqrt(variance),
    min: Math.min(...series),
    max: Math.max(...series),
  };
};

const getUniqueCount = (sites: SiteInfo[], field: keyof SiteInfo) => {
  return new Set(
    sites
      .map((site) => site[field])
      .filter((value) => typeof value === "string" && value.trim() !== ""),
  ).size;
};

const TeaesSaesCard = ({ sites }: { sites: SiteInfo[] }) => {
  const subjectsWithTeaes = sumBy(sites, "subj_with_ae_count");
  const subjectsWithSaes = sumBy(sites, "subj_with_sae_count");
  const saeRate =
    subjectsWithTeaes > 0 ? (subjectsWithSaes / subjectsWithTeaes) * 100 : 0;

  return (
    <CardLayout
      title="TEAEs & SAEs"
      subtitle="Counts of subjects with TEAEs and SAEs"
      fullHeight={true}
    >
      <div className="p-4">
        <table className="w-full text-[14px] text-[#2B4752]">
          <tbody>
            <tr>
              <td className="text-start pb-4">Subjects with TEAEs</td>
              <td className="text-end font-bold pb-4">{subjectsWithTeaes}</td>
            </tr>
            <tr>
              <td className="text-start pb-4">Subjects with SAEs</td>
              <td className="text-end font-bold pb-4">{subjectsWithSaes}</td>
            </tr>
            <tr>
              <td className="text-start">SAE Rate</td>
              <td className="text-end font-bold">{round(saeRate)}%</td>
            </tr>
          </tbody>
        </table>
      </div>
    </CardLayout>
  );
};

export function MetricsTab({ data = [] }: MetricsTabProps) {
  const sites = Array.isArray(data) ? data : [];
  const siteSDVStats = getStats(
    getNumericSeries(sites, "sdv_complete_percent_derived"),
  );
  const monthsStats = getStats(
    getNumericSeries(sites, "months_open_count_derived"),
  );
  const longOpenUnits = sites
    .map((site) => site.long_open_mvr_action_items_count_unit_derived)
    .filter((unit): unit is string => Boolean(unit));
  const longOpenUnit =
    longOpenUnits.length > 0 &&
    longOpenUnits.every((unit) => unit === longOpenUnits[0])
      ? longOpenUnits[0]
      : "> 60 days";

  const longOpenLabel = `Open ${longOpenUnit}`;

  const sitesOverviewData = {
    sites: {
      total: sites.length,
      countries: getUniqueCount(sites, "country"),
      regions: getUniqueCount(sites, "region"),
      open_stats: {
        average: round(monthsStats.average),
        std: round(monthsStats.std),
        range_low: round(monthsStats.min),
        range_high: round(monthsStats.max),
      },
    },
  };

  const enrollmentData = {
    Complete: sumBy(sites, "complete_count"),
    Active: sumBy(sites, "active_count"),
    "Early Termination": sumBy(sites, "early_termination_count"),
  };

  const screeningData = {
    Success: sumBy(sites, "screen_success_count"),
    "In Progress": sumBy(sites, "screen_in_progress_count"),
    Failure: sumBy(sites, "screen_failure_count"),
  };

  return (
    <div className="w-full mt-4 flex flex-col gap-4">
      <div className="grid lg:grid-cols-4 sm:grid-cols-2 grid-cols-1 gap-4">
        <CRFCard
          data={{
            crf: {
              overdue: sumBy(sites, "overdue_ecrfs_count_derived"),
              required_sdv: sumBy(sites, "ecrf_requiring_sdv_count_derived"),
            },
          }}
        />
        <SiteSDVCard
          data={{
            site_sdv: {
              average: round(siteSDVStats.average),
              std: round(siteSDVStats.std),
              min: round(siteSDVStats.min),
              max: round(siteSDVStats.max),
            },
          }}
        />
        <MVRActionItemsCard
          data={{
            mvr_action_items: {
              open: sumBy(sites, "open_mvr_action_items_count_derived"),
              open_60_days: sumBy(
                sites,
                "long_open_mvr_action_items_count_derived",
              ),
              closed: sumBy(sites, "closed_mvr_action_items_count_derived"),
            },
          }}
          longOpenLabel={longOpenLabel}
        />
        <TeaesSaesCard sites={sites} />
      </div>

      <div className="grid lg:grid-cols-10 sm:grid-cols-2 grid-cols-1 gap-4">
        <div className="lg:col-span-4 sm:col-span-2 col-span-1">
          <SitesOverviewCard data={sitesOverviewData} />
        </div>
        <div className="lg:col-span-3 sm:col-span-1 col-span-1 h-full max-h-[250px]">
          <CardContainer className="h-full max-h-[250px]">
            <DonutPlot
              fontSize={10}
              className="sites-enrollment"
              text={["Enrollment"]}
              keepDataOrder={true}
              data={enrollmentData}
              subtext={["All subjects at selected site(s)"]}
            />
          </CardContainer>
        </div>
        <div className="lg:col-span-3 sm:col-span-1 col-span-1 h-full max-h-[250px]">
          <CardContainer className="h-full max-h-[250px]">
            <DonutPlot
              fontSize={10}
              className="sites-screening"
              text={["Screening"]}
              keepDataOrder={true}
              data={screeningData}
              subtext={["All subjects at selected site(s)"]}
            />
          </CardContainer>
        </div>
      </div>
    </div>
  );
}
